<template>
  <div>
    <div class="px-2 py-1 d-flex flex-wrap align-items-center">
      <b-form-input
        v-model="searchQuery"
        :placeholder="$t('common.search')"
        class="mr-1 mb-50 tasks-filter"
      />
      <b-form-select
        v-model="selectedPriority"
        :options="priorityOptions"
        class="mb-50 tasks-filter"
      />
    </div>
    <b-table
      responsive="sm"
      :fields="fields"
      :items="dataList"
      striped
      :busy="loading"
      :empty-text="emptyText"
      :show-empty="!!emptyText"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle" />
          <strong class="ml-2">{{ $t('common.loading') }}</strong>
        </div>
      </template>

      <!-- Proje / İş Akışı Başlığı -->
      <template #cell(title)="data">
        <div>
          <div
            v-if="data.item.project"
            class="text-muted small"
          >
            {{ data.item.project }}
          </div>
          <router-link :to="'/app/tasks/detail/' + data.item.id">
            {{ data.item.title }}
          </router-link>
        </div>
      </template>

      <!-- Gözlemciler popover -->
      <template #cell(watchers)="data">
        <span
          :id="'watchers-' + data.item.id"
          class="cursor-pointer"
        >
          <feather-icon
            icon="EyeIcon"
            :class="data.item.watchers && data.item.watchers.length ? 'text-primary' : 'text-muted'"
          />
          <b-badge
            v-if="data.item.watchers && data.item.watchers.length"
            variant="light-primary"
            class="ml-25"
          >
            {{ data.item.watchers.length }}
          </b-badge>
        </span>
        <b-popover
          :target="'watchers-' + data.item.id"
          triggers="hover focus"
          placement="top"
        >
          <template #title>
            {{ $t('tasks.columns.watchers') }}
          </template>
          <div v-if="data.item.watchers && data.item.watchers.length">
            <div
              v-for="w in data.item.watchers"
              :key="w.id"
              class="small"
            >
              <feather-icon
                v-if="w.watcher_type === 'user'"
                icon="UserIcon"
                size="12"
                class="mr-25"
              />
              <feather-icon
                v-else
                icon="UsersIcon"
                size="12"
                class="mr-25"
              />
              {{ w.watcher_type === 'user' ? w.name : w.team }}
            </div>
          </div>
          <div
            v-else
            class="text-muted small"
          >
            {{ $t('tasks.table.noWatchers') }}
          </div>
        </b-popover>
      </template>

      <!-- Alt Görev Durumu -->
      <template #cell(subtasks)="data">
        {{ data.item.subtaskCompletedCount || 0 }} / {{ data.item.subtaskCount || 0 }}
      </template>

      <!-- Öncelik -->
      <template #cell(priority)="data">
        <priority-text :priority-id="data.item.id_task_priorities" />
      </template>

      <!-- Departman / Ekip -->
      <template #cell(dept_team)="data">
        <div class="small">
          <div v-if="data.item.department">
            {{ data.item.department }}
          </div>
          <div
            v-if="data.item.team"
            class="text-muted"
          >
            {{ data.item.team }}
          </div>
          <span v-if="!data.item.department && !data.item.team">—</span>
        </div>
      </template>

      <!-- Yönetici -->
      <template #cell(manager)="data">
        <span class="small">
          {{ managerNames(data.item) || '—' }}
        </span>
      </template>

      <!-- Hedef Tarih -->
      <template #cell(target_date)="data">
        <span>{{ data.item.target_date ? moment(data.item.target_date).format('DD.MM.YYYY') : '—' }}</span>
      </template>

      <!-- Tamamlanma Tarihi -->
      <template #cell(completed_date)="data">
        <span>{{ data.item.completed_date ? moment(data.item.completed_date).format('DD.MM.YYYY') : '—' }}</span>
      </template>

      <!-- İptal Tarihi -->
      <template #cell(cancelled_date)="data">
        <span>{{ data.item.cancelled_date ? moment(data.item.cancelled_date).format('DD.MM.YYYY') : '—' }}</span>
      </template>

      <!-- Oluşturma / Güncelleme -->
      <template #cell(created_info)="data">
        <div class="small">
          <div>{{ data.item.username || '—' }}</div>
          <div class="text-muted">
            {{ data.item.created ? moment(data.item.created).format('DD.MM.YYYY') : '—' }}
          </div>
        </div>
      </template>

      <!-- Kontrol -->
      <template #cell(control)="data">
        <div class="text-right">
          <list-buttons
            :view-action="'/app/tasks/detail/' + data.item.id"
            :edit-action="'/app/tasks/edit/' + data.item.id"
            :delete-action="canManageTask(data.item) ? removeItem : null"
            :hide-edit="isLockedItem(data.item) || !canManageTask(data.item)"
            :data-id="String(data.item.id)"
          />
        </div>
      </template>
    </b-table>

    <b-card-footer v-if="dataCounts > perPage">
      <b-pagination
        v-model="currentPage"
        :total-rows="dataCounts"
        :per-page="perPage"
        first-number
        last-number
        prev-class="prev-item"
        next-class="next-item"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="18"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="18"
          />
        </template>
      </b-pagination>
    </b-card-footer>
  </div>
</template>

<script>
import {
  BBadge, BTable, BSpinner, BPagination, BCardFooter, BPopover,
  VBPopover, BFormInput, BFormSelect,
} from 'bootstrap-vue'
import ListButtons from '@/layouts/components/common/ListButtons.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { canManageTask } from '@/auth/taskPermissions'
import PriorityText from '@/components/PriorityText.vue'
import { PRIORITY_SLUGS } from '@/libs/priorities'
import { statusSlug } from '@/libs/definitions'

const tableName = 'tasks'

export default {
  name: 'TasksTable',
  components: {
    BBadge, BTable, BSpinner, BPagination, BCardFooter, BPopover, ListButtons, PriorityText, BFormInput, BFormSelect,
  },
  directives: {
    'b-popover': VBPopover,
  },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    statusQuery: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: this.$store.state.app.perPage,
      searchQuery: '',
      selectedPriority: null,
      searchTimer: null,
    }
  },
  computed: {
    priorityOptions() {
      return [
        { value: null, text: this.$t('tasks.table.allPriorities') },
        ...Object.entries(PRIORITY_SLUGS).map(([value, slug]) => ({ value: Number(value), text: this.$t(`priorities.${slug}`) })),
      ]
    },
    loading() {
      return this.$store.getters['tasks/loading']
    },
    emptyText() {
      if (this.loading) return this.$t('common.pleaseWait')
      if (this.dataList.length === 0) return this.$t('common.noData')
      return null
    },
    dataList() {
      return this.$store.getters['tasks/dataList']
    },
    dataCounts() {
      return this.$store.getters['tasks/dataCounts']
    },
    saveData() {
      return this.$store.getters['tasks/dataSaveStatus']
    },
  },
  watch: {
    currentPage(val) {
      this.fetchData(val)
    },
    searchQuery(val) {
      clearTimeout(this.searchTimer)
      if (val.length === 1) return
      this.searchTimer = setTimeout(this.applyFilters, 400)
    },
    selectedPriority() {
      this.applyFilters()
    },
    saveData(val) {
      if (val?.status === true) {
        this.showToast({
          variant: 'success', title: this.$t('common.toast.success'), icon: 'CheckIcon', text: val.message,
        })
      } else if (val?.status === false) {
        this.showToast({
          variant: 'danger', title: this.$t('common.toast.error'), icon: 'XIcon', text: val.message,
        })
      }
      this.fetchData()
    },
  },
  created() {
    this.fetchData()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    canManageTask,
    // Sayfa 1 değilse değişiklik currentPage izleyicisiyle tek istek atar.
    applyFilters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchData(1)
      }
    },
    isLockedItem(item) {
      return ['completed', 'cancelled'].includes(statusSlug('task', item))
    },
    managerNames(item) {
      if (!item.manager_users || !item.manager_users.length) return null
      return item.manager_users.map(u => u.name).join(', ')
    },
    fetchData(page) {
      const p = page || this.currentPage
      const query = {
        select: [
          `${tableName}.id AS id`,
          `${tableName}.title AS title`,
          `${tableName}.id_users AS id_users`,
          `${tableName}.id_manager_users AS id_manager_users`,
          `${tableName}.id_task_statuses AS id_task_statuses`,
          `${tableName}.id_task_priorities AS id_task_priorities`,
          `${tableName}.target_date AS target_date`,
          `${tableName}.completed_date AS completed_date`,
          `${tableName}.cancelled_date AS cancelled_date`,
          `${tableName}.created AS created`,
          `${tableName}.modified AS modified`,
          'projects.title AS project',
          'users.name AS username',
          'departments.title AS department',
          'teams.title AS team',
        ],
        limit: this.perPage,
        start: (p - 1) * this.perPage,
        ...this.statusQuery,
      }
      if (this.selectedPriority) {
        query.where = { ...query.where, 'tasks.id_task_priorities': this.selectedPriority }
      }
      if (this.searchQuery.length >= 2) {
        query.or_like = { 'tasks.title': this.searchQuery, 'tasks.description': this.searchQuery }
      }
      this.$store.dispatch('tasks/getDataList', query)
    },
    removeItem(id) {
      this.$swal({
        title: this.$t('alerts.remove.title'),
        text: this.$t('alerts.remove.message'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('alerts.remove.confirm'),
        cancelButtonText: this.$t('alerts.remove.cancel'),
        customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-danger ml-1' },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store.dispatch('tasks/removeData', id)
          this.$swal({
            icon: 'success',
            title: this.$t('alerts.remove.resultTitle'),
            text: this.$t('alerts.remove.resultMessage'),
            confirmButtonText: this.$t('alerts.remove.resultClose'),
            customClass: { confirmButton: 'btn btn-success' },
          })
        }
      })
    },
    showToast(params) {
      this.$toast({ component: ToastificationContent, props: params })
    },
  },
}
</script>

<style scoped>
.tasks-filter {
  max-width: 240px;
}
</style>
